import React from 'react'
import {
  TableRow,
  TableCell,
  TextField,
  Checkbox,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { IUpdateUserRow } from '@/api/services/DeveloperService'
import { checkboxSx } from '../styles'

interface Props {
  row: IUpdateUserRow
  showRemove: boolean
  onPatch: (rowId: string, patch: Partial<IUpdateUserRow>) => void
  onRemove: (rowId: string) => void
}

const cellSx = { py: 1, px: 1.5, verticalAlign: 'middle' }
const inputSx = { '& .MuiInputBase-input': { fontSize: 13, py: '6px', px: '10px' } }
const disabledInputSx = { ...inputSx, '& .Mui-disabled': { backgroundColor: '#f5f2f7' } }

export const UpdateUserEntry: React.FC<Props> = ({ row, showRemove, onPatch, onRemove }) => {
  return (
    <TableRow sx={{ '&:last-child td': { borderBottom: 0 } }}>

      {/* Identifier type */}
      <TableCell sx={cellSx}>
        <Select
          value={row.identifierType}
          onChange={e => onPatch(row.rowId, {
            identifierType: e.target.value as 'userId' | 'email',
            identifierValue: '',
          })}
          size="small"
          sx={{ fontSize: 13, minWidth: 90, '& .MuiSelect-select': { py: '6px' } }}
        >
          <MenuItem value="userId" sx={{ fontSize: 13 }}>User ID</MenuItem>
          <MenuItem value="email" sx={{ fontSize: 13 }}>Email</MenuItem>
        </Select>
      </TableCell>

      {/* Identifier value */}
      <TableCell sx={cellSx}>
        <TextField
          value={row.identifierValue}
          onChange={e => onPatch(row.rowId, { identifierValue: e.target.value })}
          size="small" required
          type={row.identifierType === 'email' ? 'email' : 'text'}
          placeholder={row.identifierType === 'userId' ? 'User ID' : 'Email address'}
          sx={{ ...inputSx, minWidth: 200 }}
        />
      </TableCell>

      {/* Auto-generate */}
      <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
        <Tooltip title="Auto-generate bank account and BSB">
          <Checkbox
            size="small"
            checked={row.autoGenerate}
            onChange={e => onPatch(row.rowId, { autoGenerate: e.target.checked })}
            sx={checkboxSx}
          />
        </Tooltip>
      </TableCell>

      {/* Account number */}
      <TableCell sx={cellSx}>
        <TextField
          value={row.accountNumber}
          onChange={e => onPatch(row.rowId, { accountNumber: e.target.value })}
          size="small"
          placeholder="Account number"
          disabled={row.autoGenerate}
          sx={{ ...disabledInputSx, minWidth: 140 }}
        />
      </TableCell>

      {/* BSB */}
      <TableCell sx={cellSx}>
        <TextField
          value={row.bsb}
          onChange={e => onPatch(row.rowId, { bsb: e.target.value })}
          size="small"
          placeholder="BSB"
          disabled={row.autoGenerate}
          sx={{ ...disabledInputSx, width: 90 }}
        />
      </TableCell>

      {/* Risk percentile */}
      <TableCell sx={cellSx}>
        <TextField
          value={row.riskPercentile}
          onChange={e => onPatch(row.rowId, { riskPercentile: e.target.value })}
          size="small"
          type="number"
          placeholder="0–100"
          inputProps={{ min: 0, max: 100, step: 1 }}
          sx={{ ...inputSx, width: 80 }}
        />
      </TableCell>

      {/* Remove */}
      <TableCell sx={{ ...cellSx, textAlign: 'center' }}>
        {showRemove && (
          <Tooltip title="Remove row">
            <IconButton size="small" onClick={() => onRemove(row.rowId)} sx={{ color: '#d60000' }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </TableCell>

    </TableRow>
  )
}
